import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
// eslint-disable-next-line no-restricted-imports
import Fuse, { FuseResult, FuseResultMatch } from 'fuse.js';
import React from 'react';

import { useGetBeneficiariesQuery, useGetContactsQuery } from '@/api/v1';
import { DatePicker } from '@/components/atoms/DatePicker';
import { FormCheckbox } from '@/components/atoms/FormCheckbox';
import { HighlightedText } from '@/components/atoms/HighlightedText';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Beneficiary, SearchableBeneficiary } from '@/types/beneficiary';
import { Contact, SearchableContact } from '@/types/contacts';
import { CurrencyCodeType } from '@/types/currency';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { Page } from '@/types/page';
import { SimplifiedTransactionStatus, TransactionType } from '@/types/transaction';
import { TransactionFilterField } from '@/types/transactionFilterField';
import { DATE_DISPLAY_FORMAT, toEndOfDayUTC, toStartOfDayUTC } from '@/utility/dateFormats';
import { noop } from '@/utility/noop';
import { toBeneficiary } from '@/utility/toBeneficiary';

export interface DatePreset {
  readonly label: string;
  readonly getValue: () => { readonly fromDate: Date; readonly toDate: Date };
}

interface Props {
  readonly filters: GetTransactionsFilters;
  readonly visibleFilters: readonly TransactionFilterField[];
  readonly datePresets?: readonly DatePreset[];
  onChange(filter: GetTransactionsFilters): void;
}

interface FilteredContact {
  readonly item: SearchableContact;
  readonly matchIndices?: ReadonlyArray<readonly [number, number]>;
}

interface FilteredBeneficiary {
  readonly item: SearchableBeneficiary;
  readonly matchIndices?: ReadonlyArray<readonly [number, number]>;
}

type ChangeFn = (event: React.ChangeEvent<HTMLInputElement>) => void;

export const TransactionsFilters: React.FC<Props> = (props: Props): React.ReactElement => {
  const customer = useCustomer();
  const [preferences] = useUserPreferences();
  const timezone = preferences.timezone;

  const { filters, visibleFilters, datePresets, onChange } = props;

  const isFilterVisible = (field: TransactionFilterField): boolean =>
    visibleFilters.includes(field);
  const orderOf = (field: TransactionFilterField): number => visibleFilters.indexOf(field);
  const { data: unfilteredRecipientsPage = Page.empty<Contact>() } = useGetContactsQuery(
    { beneficiaryId: customer.beneficiaryId, size: 1000 },
    { skip: customer.beneficiaryId === null },
  );
  const unfilteredRecipients = unfilteredRecipientsPage.content;
  const { data: unfilteredBeneficiariesPage } = useGetBeneficiariesQuery();
  const unfilteredBeneficiaries = React.useMemo(
    (): readonly Beneficiary[] => (unfilteredBeneficiariesPage?.content ?? []).map(toBeneficiary),
    [unfilteredBeneficiariesPage],
  );

  const [recipientSearchKeyword, setRecipientSearchKeyword] = React.useState<string>('');
  const [beneficiarySearchKeyword, setBeneficiarySearchKeyword] = React.useState<string>('');
  const [data, setData] = React.useState<GetTransactionsFilters>(filters);

  const activePreset = React.useMemo((): string | null => {
    if (!datePresets) {
      return null;
    }
    for (const preset of datePresets) {
      const { fromDate: from, toDate: to } = preset.getValue();
      if (
        data.fromDate === toStartOfDayUTC(from, timezone) &&
        data.toDate === toEndOfDayUTC(to, timezone)
      ) {
        return preset.label;
      }
    }
    return null;
  }, [datePresets, data.fromDate, data.toDate, timezone]);

  const tr = useTranslator();
  const searchableUnfilteredContacts = React.useMemo(
    (): readonly SearchableContact[] =>
      unfilteredRecipients.map(
        (contact: Contact): SearchableContact => ({
          id: contact.id,
          displayName: Contact.getName(contact),
        }),
      ),
    [unfilteredRecipients],
  );

  const recipientFuse = React.useMemo((): Fuse<SearchableContact> => {
    return new Fuse(searchableUnfilteredContacts, {
      keys: ['displayName'],
      threshold: 0.3,
      includeMatches: true,
    });
  }, [searchableUnfilteredContacts]);

  const recipients = React.useMemo((): readonly FilteredContact[] => {
    if (recipientSearchKeyword.trim() !== '') {
      const results = recipientFuse.search(recipientSearchKeyword);
      return results.map((result: FuseResult<SearchableContact>): FilteredContact => {
        const match = result.matches?.find(
          (m: FuseResultMatch): boolean => m.key === 'displayName',
        );
        return {
          item: result.item,
          matchIndices: match?.indices as ReadonlyArray<readonly [number, number]> | undefined,
        };
      });
    }

    return searchableUnfilteredContacts.map(
      (item: SearchableContact): FilteredContact => ({ item }),
    );
  }, [recipientFuse, recipientSearchKeyword, searchableUnfilteredContacts]);

  const searchableUnfilteredBeneficiaries = React.useMemo(
    (): readonly SearchableBeneficiary[] =>
      unfilteredBeneficiaries.map(
        (beneficiary: Beneficiary): SearchableBeneficiary => ({
          id: beneficiary.id,
          displayName: beneficiary.name,
        }),
      ),
    [unfilteredBeneficiaries],
  );

  const beneficiaryFuse = React.useMemo((): Fuse<SearchableBeneficiary> => {
    return new Fuse(searchableUnfilteredBeneficiaries, {
      keys: ['displayName'],
      threshold: 0.3,
      includeMatches: true,
    });
  }, [searchableUnfilteredBeneficiaries]);

  const beneficiaries = React.useMemo((): readonly FilteredBeneficiary[] => {
    if (beneficiarySearchKeyword.trim() !== '') {
      const results = beneficiaryFuse.search(beneficiarySearchKeyword);

      return results.map((result: FuseResult<SearchableBeneficiary>): FilteredBeneficiary => {
        const match = result.matches?.find(
          (m: FuseResultMatch): boolean => m.key === 'displayName',
        );
        return {
          item: result.item,
          matchIndices: match?.indices as ReadonlyArray<readonly [number, number]> | undefined,
        };
      });
    }

    return searchableUnfilteredBeneficiaries.map(
      (item: SearchableBeneficiary): FilteredBeneficiary => ({ item }),
    );
  }, [beneficiaryFuse, beneficiarySearchKeyword, searchableUnfilteredBeneficiaries]);

  const handleDateChange = React.useCallback(
    (field: keyof GetTransactionsFilters): ((date: Date | null) => void) => {
      return (date: Date | null): void => {
        if (!date) {
          setData(
            (localState: GetTransactionsFilters): GetTransactionsFilters => ({
              ...localState,
              [field]: '',
            }),
          );
        } else {
          const toUTC = field === 'toDate' ? toEndOfDayUTC : toStartOfDayUTC;
          setData(
            (localState: GetTransactionsFilters): GetTransactionsFilters => ({
              ...localState,
              [field]: toUTC(date, timezone),
            }),
          );
        }
      };
    },
    [timezone],
  );

  const handleChange = React.useCallback((field: keyof GetTransactionsFilters): ChangeFn => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;

      setData(
        (localState: GetTransactionsFilters): GetTransactionsFilters => ({
          ...localState,
          [field]: value,
        }),
      );

      if (field === 'recipientId') {
        setRecipientSearchKeyword('');
      } else if (field === 'beneficiaryId') {
        setBeneficiarySearchKeyword('');
      }
    };
  }, []);

  const fromDate = React.useMemo(
    (): Date | null =>
      data.fromDate === '' ? null : toZonedTime(parseISO(data.fromDate), timezone),
    [data.fromDate, timezone],
  );

  const toDate = React.useMemo(
    (): Date | null => (data.toDate === '' ? null : toZonedTime(parseISO(data.toDate), timezone)),
    [data.toDate, timezone],
  );

  const onApply = React.useCallback((): void => {
    onChange(data);
  }, [onChange, data]);

  const fromDateResetRef = React.useRef<VoidFunction>(noop);
  const toDateResetRef = React.useRef<VoidFunction>(noop);

  const onReset = React.useCallback((): void => {
    const { current: resetFromDate } = fromDateResetRef;
    const { current: resetToDate } = toDateResetRef;

    resetFromDate?.();
    resetToDate?.();

    setData(GetTransactionsFilters.initial());

    onChange(GetTransactionsFilters.initial());
  }, [onChange]);

  const onSearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: keyword } = event.target;
      setRecipientSearchKeyword(keyword);
    },
    [],
  );

  const onBeneficiarySearchKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: keyword } = event.target;
      setBeneficiarySearchKeyword(keyword);
    },
    [],
  );

  const handlePresetClick = React.useCallback(
    (preset: DatePreset): void => {
      const { fromDate: from, toDate: to } = preset.getValue();

      setData(
        (localState: GetTransactionsFilters): GetTransactionsFilters => ({
          ...localState,
          fromDate: toStartOfDayUTC(from, timezone),
          toDate: toEndOfDayUTC(to, timezone),
        }),
      );
    },
    [timezone],
  );

  return (
    <Stack>
      <Box sx={styles.container}>
        {isFilterVisible(TransactionFilterField.TRANSACTION_TYPE) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.TRANSACTION_TYPE) }}
            select={true}
            label={tr('TRANSACTION_TYPE')}
            value={data.type}
            fullWidth={true}
            onChange={handleChange('type')}>
            <MenuItem value={TransactionType.ALL}>{tr('ALL')}</MenuItem>
            <MenuItem value={TransactionType.TRANSFER}>{tr('TRANSFER')}</MenuItem>
            <MenuItem value={TransactionType.WITHDRAWAL}>{tr('WITHDRAWAL')}</MenuItem>
            <MenuItem value={TransactionType.DEPOSIT}>{tr('DEPOSIT')}</MenuItem>
            <MenuItem value={TransactionType.INTERNAL_TRANSFER}>{tr('INTERNAL_TRANSFER')}</MenuItem>
            <MenuItem value={TransactionType.REFUND}>{tr('REFUND')}</MenuItem>
          </TextField>
        )}
        {isFilterVisible(TransactionFilterField.STATUS) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.STATUS) }}
            select={true}
            label={tr('STATUS')}
            value={data.status}
            fullWidth={true}
            onChange={handleChange('status')}>
            <MenuItem value={SimplifiedTransactionStatus.ALL}>{tr('ALL')}</MenuItem>
            <MenuItem value={SimplifiedTransactionStatus.IN_PROGRESS}>{tr('IN_PROGRESS')}</MenuItem>
            <MenuItem value={SimplifiedTransactionStatus.COMPLETED}>{tr('COMPLETED')}</MenuItem>
            <MenuItem value={SimplifiedTransactionStatus.FAILED}>{tr('FAILED')}</MenuItem>
          </TextField>
        )}
        {datePresets && datePresets.length > 0 && (
          <Stack
            direction="row"
            gap={1}
            sx={{ order: orderOf(TransactionFilterField.FROM_DATE), gridColumn: 'span 2' }}>
            {datePresets.map(
              (preset: DatePreset): React.ReactElement => (
                <Button
                  key={preset.label}
                  variant={activePreset === preset.label ? 'dark' : 'outlined'}
                  size="small"
                  sx={
                    activePreset === preset.label
                      ? undefined
                      : { color: 'text.primary', borderColor: 'text.primary' }
                  }
                  onClick={(): void => handlePresetClick(preset)}>
                  {preset.label}
                </Button>
              ),
            )}
          </Stack>
        )}
        {isFilterVisible(TransactionFilterField.FROM_DATE) && (
          <Box sx={{ order: orderOf(TransactionFilterField.FROM_DATE) }}>
            <DatePicker
              label={tr('FROM_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              reset={fromDateResetRef}
              value={fromDate}
              format={DATE_DISPLAY_FORMAT}
              onChange={handleDateChange('fromDate')}
            />
          </Box>
        )}
        {isFilterVisible(TransactionFilterField.TO_DATE) && (
          <Box sx={{ order: orderOf(TransactionFilterField.TO_DATE) }}>
            <DatePicker
              label={tr('TO_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              reset={toDateResetRef}
              value={toDate}
              format={DATE_DISPLAY_FORMAT}
              onChange={handleDateChange('toDate')}
            />
          </Box>
        )}
        {isFilterVisible(TransactionFilterField.AMOUNT) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.AMOUNT) }}
            label={tr('AMOUNT')}
            value={data.amount}
            fullWidth={true}
            onChange={handleChange('amount')}
          />
        )}
        {isFilterVisible(TransactionFilterField.CURRENCY) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.CURRENCY) }}
            select={true}
            label={tr('CURRENCY_TYPE')}
            value={data.currency}
            fullWidth={true}
            onChange={handleChange('currency')}>
            <MenuItem value={CurrencyCodeType.ANY}>{tr('ANY')}</MenuItem>
            <MenuItem value={CurrencyCodeType.USD}>USD</MenuItem>
            <MenuItem value={CurrencyCodeType.BRL}>BRL</MenuItem>
            <MenuItem value={CurrencyCodeType.MXN}>MXN</MenuItem>
          </TextField>
        )}
        {isFilterVisible(TransactionFilterField.SENDER) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.SENDER) }}
            select
            label={tr('SENDER')}
            value={data.beneficiaryId}
            fullWidth
            onChange={handleChange('beneficiaryId')}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                displayEmpty: true,
                renderValue: (value: unknown): React.ReactNode => {
                  if (!value) {
                    return tr('ANY');
                  }
                  const filtered = beneficiaries.find(
                    (filtered: FilteredBeneficiary): boolean => filtered.item.id === value,
                  );
                  return filtered?.item.displayName ?? String(value);
                },
              },
            }}>
            <SearchMenuItem onChange={onBeneficiarySearchKeywordChange} />
            <MenuItem value="">{tr('ANY')}</MenuItem>
            {beneficiaries.map(
              (filtered: FilteredBeneficiary): React.ReactElement => (
                <MenuItem key={filtered.item.id} value={filtered.item.id}>
                  <HighlightedText
                    text={filtered.item.displayName}
                    indices={filtered.matchIndices}
                  />
                </MenuItem>
              ),
            )}
          </TextField>
        )}
        {isFilterVisible(TransactionFilterField.RECIPIENT) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.RECIPIENT) }}
            select={true}
            label={tr('RECIPIENT')}
            value={data.recipientId}
            fullWidth={true}
            onChange={handleChange('recipientId')}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                displayEmpty: true,
                renderValue: (value: unknown): React.ReactNode => {
                  if (!value) {
                    return tr('ANY');
                  }
                  const filtered = recipients.find(
                    (filtered: FilteredContact): boolean => filtered.item.id === value,
                  );
                  return filtered?.item.displayName ?? String(value);
                },
              },
            }}>
            <SearchMenuItem onChange={onSearchKeywordChange} />
            <MenuItem value="">{tr('ANY')}</MenuItem>
            {recipients.map(
              (filtered: FilteredContact): React.ReactElement => (
                <MenuItem key={filtered.item.id} value={filtered.item.id}>
                  <HighlightedText
                    text={filtered.item.displayName}
                    indices={filtered.matchIndices}
                  />
                </MenuItem>
              ),
            )}
          </TextField>
        )}
        {isFilterVisible(TransactionFilterField.TRANSACTION_ID) && (
          <TextField
            sx={{ order: orderOf(TransactionFilterField.TRANSACTION_ID) }}
            label={tr('Transaction ID # (UETR)')}
            value={data.id}
            fullWidth={true}
            onChange={handleChange('id')}
          />
        )}
        {isFilterVisible(TransactionFilterField.AWAITING_FUNDING) && (
          <Box
            sx={{
              order: orderOf(TransactionFilterField.AWAITING_FUNDING),
              gridColumn: 'span 2',
            }}>
            <FormCheckbox
              label={tr('AWAITING_FUNDING_FILTER_LABEL')}
              value={String(data.awaitingFunding) === 'true'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                const { checked } = event.target;
                setData(
                  (localState: GetTransactionsFilters): GetTransactionsFilters => ({
                    ...localState,
                    awaitingFunding: checked ? true : undefined,
                  }),
                );
              }}
            />
          </Box>
        )}
      </Box>
      <Stack alignItems="center" mt={6} gap={3}>
        <Button variant="contained" fullWidth onClick={onApply}>
          {tr('APPLY')}
        </Button>
        <Button variant="text" sx={styles.textButton} disableRipple onClick={onReset}>
          {tr('RESET')}
        </Button>
      </Stack>
    </Stack>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: '1fr 1fr',
  },
  textButton: {
    color: 'text.primary',
  },
  spanned: {
    gridColumn: 'span 2',
  },
};
